// Homepage experience catalogue — sourced from Website_08.08.xlsx (Aug 2026).
// UI-only fields (slug, image, theme, marketplaceUrl) are maintained here alongside the sheet copy.

export type HomepageDestination = 'Thanjavur' | 'Kancheepuram' | 'Coimbatore'
export type ExperienceTheme = 'craft' | 'food' | 'land' | 'heritage' | 'music'

export interface HomepageExperience {
  slug: string
  marketplaceUrl: string
  /** Experience Name */
  title: string
  /** Destination column */
  location: string
  /** District column — used for destination tabs */
  district: HomepageDestination
  duration: string
  hostName?: string
  timeSlot?: string
  /** Experience in One Line */
  oneLine: string
  /** What Stays With You */
  staysWithYou: string
  /** Experience 1–3 */
  description: [string, string, string]
  image: string
  theme: ExperienceTheme
  hotspots: Array<[string, string]>
}

function experience(
  input: Omit<HomepageExperience, 'hotspots'> & { hotspots?: Array<[string, string]> },
): HomepageExperience {
  return {
    ...input,
    hotspots: input.hotspots ?? [
      [input.location.trim(), 'Destination'],
      [input.district, 'District'],
    ],
  }
}

export const homepageExperiences: HomepageExperience[] = [
  experience({
    slug: 'thirubuvanam-silk-weaving',
    marketplaceUrl: 'https://app.oorva.in/experiences/silk-weaving',
    // Card copy kept in sync with the canonical editorial page
    // (/experience/thirubhuvanam-silk-weaving). The slug keeps its historic
    // spelling; getHomepageExperiencePath maps it to the canonical URL.
    title: 'Thirubhuvanam Silk Weaving',
    location: 'Thirubhuvanam',
    district: 'Thanjavur',
    duration: '1–1.5 hours',
    hostName: 'Jayanthi',
    timeSlot: '12.00 PM - 1.00 PM',
    oneLine:
      'Enter a traditional weaving home and witness how exquisite silk sarees are brought to life, one thread at a time.',
    staysWithYou:
      'The remarkable dedication, artistry, and heritage woven into every Thirubuvanam silk saree.',
    description: [
      'Meet local weaving families and explore the journey of silk from yarn preparation to the finished masterpiece.',
      'Watch skilled artisans operate the handloom and take a tour to the cooperative society to understand different processes of weaving',
      'Learn about the distinctive patterns, weaving techniques, and traditions that have made Thirubuvanam silk renowned across generations.',
    ],
    image: '/weekend-trips/silk-weaving-loom.webp',
    theme: 'craft',
  }),
  experience({
    slug: 'kumbakonam-betel-farm-walk',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Thanjavur&q=Betel+Farm+walk',
    // Card copy kept in sync with the canonical editorial page
    // (/experience/kumbakonam-betel-leaf-farm).
    title: 'Kumbakonam Betel Leaf Farm Walk',
    location: 'Kumbakonam',
    district: 'Thanjavur',
    duration: '1–1.5 hours',
    hostName: 'Ravi',
    timeSlot: '3.00 PM - 3.25 PM',
    oneLine:
      "Wander through lush betel vine gardens and uncover the stories behind one of Tamil Nadu's most treasured crops.",
    staysWithYou:
      'A deeper appreciation for the care, patience, and tradition behind every vibrant betel leaf.',
    description: [
      'Walk through the shaded betel farms with local farmers and learn about cultivation, irrigation, and harvesting techniques.',
      'Try harvesting fresh betel leaves, understand their cultural significance, and discover their role in everyday life and celebrations.',
      'Interact with the farming family, enjoy refreshing local hospitality, and experience the peaceful charm of the countryside.',
    ],
    image: '/weekend-trips/betel-farm-corridor.webp',
    theme: 'land',
  }),
  experience({
    slug: 'karuppur-kalamkari',
    marketplaceUrl: 'https://app.oorva.in/experiences/karuppur-kalamkari-painting',
    // Card copy kept in sync with the canonical editorial page
    // (/experience/kalamkari-painting).
    title: 'Kalamkari Painting',
    location: 'Thanjavur',
    district: 'Thanjavur',
    duration: '1–1.5 hours',
    hostName: 'Raj Mohan',
    timeSlot: '11.00 AM - 12.00 PM',
    oneLine:
      'Discover the intricate art of hand-painted temple textiles through a tradition rooted in devotion and craftsmanship.',
    staysWithYou:
      'The beauty of transforming a simple piece of fabric into a work of art using age-old techniques.',
    description: [
      'Meet master artisans and learn the history, symbolism, and cultural significance of Karuppur Kalamkari.',
      'Experience the process of sketching, natural colouring, and hand painting on fabric using traditional methods.',
      'Create your own Kalamkari artwork under expert guidance and take home a piece of this living heritage.',
    ],
    image: '/experiences/karuppur-kalamkari-dyes.jpg',
    theme: 'craft',
  }),
  experience({
    slug: 'thanjavur-thalaiyatti-bommai',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Thanjavur&q=Thanjavur+Thalaiyatti+Bommai',
    // Card copy kept in sync with the canonical editorial page
    // (/experience/thanjavur-doll-making).
    title: 'Thanjavur Doll Making',
    location: 'Thanjavur',
    district: 'Thanjavur',
    duration: '1–1.5 hours',
    hostName: 'Sivakumar',
    timeSlot: '11.00 AM - 12.00 PM',
    oneLine:
      "Step into the world of Thanjavur's iconic dancing dolls and create one alongside the artisans who have preserved this craft for generations.",
    staysWithYou: 'The joy of crafting a timeless symbol of Tamil heritage with your own hands.',
    description: [
      "Visit an artisan's workshop and discover the history and cultural significance of the famous Thalaiyatti Bommai.",
      'Learn the intricate process of moulding, balancing, painting, and decorating the doll with guidance from skilled artisans.',
      'Paint your own miniature Thalaiyatti Bommai, interact with the artisan family, and take home a handcrafted souvenir.',
    ],
    image: '/weekend-trips/doll-making-finished.png',
    theme: 'craft',
  }),
  experience({
    slug: 'sirumugai-silk-weaving',
    marketplaceUrl: 'https://app.oorva.in/experiences/silk-weaving',
    title: 'Sirumugai Silk Weaving',
    location: 'Sirumugai',
    district: 'Coimbatore',
    duration: '1–1.5 hours',
    hostName: 'Gopalakrishnan',
    timeSlot: '11.00 AM - 12.00 PM',
    oneLine:
      'Step into the weaving homes of Sirumugai and witness the craftsmanship behind its beautiful silk sarees.',
    staysWithYou:
      'The patience, precision, and passion woven into every thread by generations of skilled artisans.',
    description: [
      'Meet local weaving families and discover the journey of silk from raw yarn to elegant sarees.',
      'Watch the handloom in action and try weaving a few lines under the guidance of experienced artisans.',
      'Learn about the unique designs, techniques, and stories that have made Sirumugai silk a cherished weaving tradition.',
    ],
    image: '/experiences/sungudi-saree-weaving.jpg',
    theme: 'craft',
  }),
  experience({
    slug: 'thudumbattam-banana-plantation',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Coimbatore&q=Thudumbattam+Drums+crafting',
    title: 'Thudumbattam in Banana Plantation',
    location: 'Sirumugai',
    district: 'Coimbatore',
    duration: '1–1.5 hours',
    hostName: 'Saminathan',
    timeSlot: '12.00 PM - 1.00 PM',
    oneLine:
      'Walk through a thriving banana plantation and experience the vibrant beats of the traditional Thudumbattam amidst nature.',
    staysWithYou:
      'The unforgettable rhythm of folk music echoing through the green fields and the warmth of village hospitality.',
    description: [
      'Take a guided walk through the banana plantation and learn about cultivation from local farmers.',
      'Witness an energetic live Thudumbattam performance in the heart of the farm and discover its cultural significance.',
      'Join the performers, try the traditional percussion, interact with the artists, and capture memorable moments in the plantation.',
    ],
    image: '/showcase-image-6.jpeg',
    theme: 'music',
  }),
  experience({
    slug: 'neera-tapping',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Coimbatore&q=Neera+Tapping',
    title: 'Neera Tapping Experience',
    location: 'Palladam',
    district: 'Coimbatore',
    duration: '1–1.5 hours',
    hostName: 'Elango',
    timeSlot: '8.30 AM - 9.15 AM',
    oneLine:
      'Witness the fascinating art of harvesting fresh coconut nectar and discover a tradition passed down through generations.',
    staysWithYou:
      'A newfound respect for the skill, balance, and dedication of the coconut climbers who keep this tradition alive.',
    description: [
      'Walk through the coconut groves and watch experienced tappers climb towering coconut palms using traditional techniques.',
      'Learn how fresh Neera is collected, understand its health benefits, and taste it fresh from the source',
      'Interact with the local community and uncover the stories, challenges, and heritage behind this unique livelihood.',
    ],
    image: '/experiences/coconut-neera-tapping.jpg',
    theme: 'land',
  }),
  experience({
    slug: 'kancheepuram-silk-weaving',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Kancheepuram&q=Kancheepuram+Silk+Weaving',
    title: 'Kanchipuram Silk Weaving Experience',
    location: 'Kanchipuram Town',
    district: 'Kancheepuram',
    duration: '1–1.5 hours',
    timeSlot: '11.00 AM - 12.00 PM',
    oneLine: "Walk into a weaver's home and witness how one of India's most celebrated silk sarees comes to life.",
    staysWithYou:
      'A newfound admiration for the patience, precision, and artistry woven into every Kanchipuram silk saree.',
    description: [
      'Meet master weavers, hear their stories, and explore the journey of silk from yarn to loom.',
      'Experience the weaving process up close and try weaving a few lines under the guidance of skilled artisans.',
      'Discover the intricate motifs, vibrant colours, and craftsmanship that make every Kanchipuram saree a timeless masterpiece.',
    ],
    image: '/experiences/kanchivaram-silk-weaving.jpg',
    theme: 'craft',
  }),
  experience({
    slug: 'therukoothu',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Kancheepuram&q=Therukoothu',
    title: 'Therukoothu Experience',
    location: 'Kanchipuram Town',
    district: 'Kancheepuram',
    duration: '1–1.5 hours',
    hostName: 'Hannae Rajagopal',
    timeSlot: '11.00 AM - 1.00 PM',
    oneLine:
      "Step into the vibrant world of Therukoothu and experience one of Tamil Nadu's oldest folk theatre traditions.",
    staysWithYou:
      'The thrill of becoming part of a centuries-old storytelling tradition through costume, performance, and expression.',
    description: [
      'Learn the basics of Therukoothu, including its history, movements, facial expressions, and storytelling techniques from experienced artists.',
      'Watch the costume of Therukoothu artists and experience the traditional makeup and character transformation.',
      'Watch the live performance of artists in a short Therukoothu act and understand the cultural significance of this living folk art.',
    ],
    // TODO: real Therukoothu performance photo (e.g. /experiences/therukoothu.jpg).
    // Interim: real Nadaswaram workshop shot — same performance tradition.
    image: '/experiences/nadaswaram-pipes.jpg',
    theme: 'heritage',
  }),
  experience({
    slug: 'traditional-kanchipuram-idli',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Kancheepuram&q=Kanchi+Koil+Idli+making',
    title: 'Traditional Kanchipuram Idli Experience',
    location: 'Kanchipuram Town',
    district: 'Kancheepuram',
    duration: '1–1.5 hours',
    timeSlot: '12.00 PM - 1.00 PM',
    oneLine:
      'Go beyond tasting Kanchipuram Idli - learn the centuries-old recipe and the traditions that make it unique.',
    staysWithYou:
      'The joy of recreating an authentic temple-style delicacy and the stories served with every bite.',
    description: [
      'Step into a local kitchen and discover the history, spices, and preparation behind the iconic Kanchipuram Idli.',
      'Grind, mix, and steam the batter using traditional methods while learning family recipes passed down through generations.',
      'Enjoy the freshly prepared idlis with local accompaniments and share a meal with your hosts.',
    ],
    // TODO: real Kanchipuram idli-making photo (e.g. /experiences/kanchi-idli-making.jpg).
    // Interim: Kuzhi Paniyaram on a firewood stove — same Tamil kitchen setting.
    image: '/experiences/kuzhi-paniyaram-making-with-paati.jpg',
    theme: 'food',
  }),
  experience({
    slug: 'paddy-field-experience',
    marketplaceUrl: 'https://app.oorva.in/?state=Tamil+Nadu&district=Kancheepuram&q=Thooyamalli+Paddy+Walk',
    title: 'Paddy Field Experience',
    location: 'Kanchipuram Town',
    district: 'Kancheepuram',
    duration: '1–1.5 hours',
    timeSlot: '11.00 AM - 12.00 PM',
    oneLine: 'Step into the paddy fields and experience the rhythm of rural life alongside local farmers.',
    staysWithYou:
      'A deeper appreciation for the people, traditions, and effort behind every grain of rice.',
    description: [
      'Walk through lush paddy fields and learn about the seasonal cultivation cycle from local farmers.',
      'Take part in hands-on farming activities such as planting, harvesting, or field preparation based on the season.',
      'Relax by the fields, interact with the farming community, and discover the stories and traditions that shape village life.',
    ],
    image: '/experiences/paddy-harvesting-in-wet-fields.jpg',
    theme: 'land',
  }),
]

export const homepageDestinations: HomepageDestination[] = ['Thanjavur', 'Coimbatore', 'Kancheepuram']

export const destinationExperiences: Record<HomepageDestination, HomepageExperience[]> = {
  Thanjavur: homepageExperiences.filter((item) => item.district === 'Thanjavur'),
  Coimbatore: homepageExperiences.filter((item) => item.district === 'Coimbatore'),
  Kancheepuram: homepageExperiences.filter((item) => item.district === 'Kancheepuram'),
}

const editorialSlugByDiscoverySlug: Partial<Record<string, string>> = {
  'thirubuvanam-silk-weaving': 'thirubhuvanam-silk-weaving',
  'kumbakonam-betel-farm-walk': 'kumbakonam-betel-leaf-farm',
  'karuppur-kalamkari': 'kalamkari-painting',
  'thanjavur-thalaiyatti-bommai': 'thanjavur-doll-making',
}

export function getHomepageExperiencePath(slug: string): string {
  // Single canonical base: every experience lives at /experience/[slug].
  // /discover/[slug] only redirects there (see src/app/discover/[slug]).
  // Discovery slugs with an editorial counterpart map to the editorial slug;
  // everything else (including editorial slugs themselves) maps to itself.
  const editorialSlug = editorialSlugByDiscoverySlug[slug]
  return `/experience/${editorialSlug ?? slug}`
}

export function hasEditorialExperiencePage(slug: string): boolean {
  return Boolean(editorialSlugByDiscoverySlug[slug])
}

export function getHomepageExperienceBySlug(slug: string): HomepageExperience | undefined {
  const legacySlugMap: Record<string, string> = {
    'kanchi-koil-idli-making': 'traditional-kanchipuram-idli',
    'thooyamalli-paddy-walk': 'paddy-field-experience',
    'thudumbattam-drums-crafting': 'thudumbattam-banana-plantation',
    'banana-plantation': 'thudumbattam-banana-plantation',
    'kumbakonam-betel-leaf-farm': 'kumbakonam-betel-farm-walk',
    'thanjavur-doll-making': 'thanjavur-thalaiyatti-bommai',
    'kalamkari-painting': 'karuppur-kalamkari',
  }

  const resolvedSlug = legacySlugMap[slug] ?? slug
  return homepageExperiences.find((item) => item.slug === resolvedSlug)
}
